import React, { useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import japetoLogo from '../../assets/images/japeto-logo-light.svg';
import EmbedContext from '../../context/EmbedContext/EmbedContext';
import useChatbotEmbed from '../../hooks/useChatbotEmbed';
import CustomSelect from '../../components/CustomSelect/CustomSelect';

function BotTestPage(): React.ReactElement {
  const { embedIsReady, setContextChatbotUuid, setEmbedIsReady } =
    useContext(EmbedContext);
  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(location.search);
  const embedTypeFromQuery = queryParams.get('embedType');
  const [chatbotEmbedType, setChatbotEmbedType] = useState<
    'Regular' | 'Full screen'
  >(embedTypeFromQuery === 'Full screen' ? 'Full screen' : 'Regular');

  const { chatbotUUID } = useParams();
  const isTestFromQuery = queryParams.get('isTest') === 'true';
  const [isTestMode, setIsTestMode] = useState<boolean>(isTestFromQuery);

  const handleChangeTestMode = (value: string): void => {
    const newMode = value === 'true';
    setIsTestMode(newMode);

    queryParams.set('isTest', String(newMode));
    navigate(`${location.pathname}?${queryParams.toString()}`, {
      replace: true
    });
  };

  const handleChangeChatbotEmbedType = (value: string): void => {
    const newType = value as 'Regular' | 'Full screen';
    setChatbotEmbedType(newType);

    queryParams.set('embedType', newType);
    navigate(`${location.pathname}?${queryParams.toString()}`, {
      replace: true
    });
  };

  useChatbotEmbed(
    chatbotUUID as string,
    isTestMode,
    setEmbedIsReady,
    chatbotEmbedType
  );

  useEffect(() => {
    if (chatbotUUID) {
      setContextChatbotUuid(chatbotUUID);
    }
  }, [chatbotUUID, setContextChatbotUuid]);

  const showChatbot = (): void => {
    window.japetoApi?.showChatbot();
  };

  const hideChatbot = (): void => {
    window.japetoApi?.hideChatbot();
  };

  useEffect(() => {
    if (embedIsReady) {
      showChatbot();
    }
    return () => {
      if (embedIsReady) {
        hideChatbot();
      }
    };
  }, [embedIsReady]);

  return (
    <div className='min-h-screen min-w-screen flex items-center justify-center bg-primary-dark'>
      <Helmet>
        <title>Japeto - Bot Test Page</title>
      </Helmet>
      <div className='p-4 rounded-lg absolute top-4 left-4 bg-primary z-10 w-48 shadow-lg [&_label]:text-white [&>*:last-child]:mb-0'>
        <CustomSelect
          label='Chatbot mode'
          value={String(isTestMode)}
          onValueChange={handleChangeTestMode}
          options={[
            { value: 'false', label: 'Live' },
            { value: 'true', label: 'Test' }
          ]}
          className='bg-white'
          fullWidth
        />
        <CustomSelect
          label='Chatbot type'
          value={chatbotEmbedType}
          onValueChange={handleChangeChatbotEmbedType}
          options={[
            { value: 'Regular', label: 'Regular' },
            { value: 'Full screen', label: 'Full screen' }
          ]}
          className='bg-white'
          fullWidth
        />
      </div>
      <img src={japetoLogo} alt='Japeto Logo' className='h-28 w-auto' />
    </div>
  );
}

export default BotTestPage;
